/**
 * A company's identity as generated documents need it: who it is legally, how
 * to reach it, and the colors and logo its paperwork wears.
 *
 * Everything here resolves to a solid `#rrggbb`. The proposal PDF is produced
 * by rasterizing the document's DOM (see `./pdf.ts`), and that renderer parses
 * neither `oklch()` — which the app's own palette is written in — nor the
 * `color-mix()` a Tailwind opacity modifier compiles to. A branded document
 * therefore carries inline styles from `brandPalette` rather than generated
 * utility classes, and the shades are derived here instead of being asked of
 * the browser.
 *
 * The app's chrome is deliberately untouched by any of this. One palette for
 * the product, one per tenant for the documents it prints, so an operator
 * moving between companies can always tell which is which.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, unwrap } from "./api";

export type CompanyIdentity = {
  name: string;
  legalName: string | null;
  ein: string | null;
  phone: string | null;
  email: string | null;
  brandColor: string | null;
  logoUrl: string | null;
  displayFontName: string | null;
  displayFontSrc: string | null;
  bodyFontName: string | null;
  bodyFontSrc: string | null;
};

/** A company with nothing set — what a document falls back to while identity
 *  is still loading, and what a brand-new company prints as. */
export const EMPTY_IDENTITY: CompanyIdentity = {
  name: "",
  legalName: null,
  ein: null,
  phone: null,
  email: null,
  brandColor: null,
  logoUrl: null,
  displayFontName: null,
  displayFontSrc: null,
  bodyFontName: null,
  bodyFontSrc: null,
};

/** What a company that has never opened the branding form prints as. */
export const DEFAULT_BRAND_COLOR = "#4338ca";

export type BrandPalette = {
  /** Fills — table headers, the rule under the letterhead. */
  base: string;
  /** Text and marks sitting on `base`. */
  onBase: string;
  /** `base` pushed dark enough to read as text on white. */
  ink: string;
  /** `base` mixed far into white — a fill that tints without shouting. */
  wash: string;
};

type Rgb = [number, number, number];

function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m?.[1]) return null;
  const n = Number.parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** WCAG relative luminance — the basis for both the readability decisions below. */
function luminance([r, g, b]: Rgb): number {
  const f = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

/**
 * Derives a document's whole palette from the one color a company chose.
 *
 * A company will pick the color from its logo, and that color may be anything
 * from pale gold to near-black — so nothing here assumes a mid-tone. Text
 * shades are darkened until they clear a luminance bar rather than being
 * offset by a fixed amount, which is what keeps a yellow brand legible on
 * white instead of merely lighter.
 */
export function brandPalette(brandColor: string | null | undefined): BrandPalette {
  const base = parseHex(brandColor ?? "") ?? (parseHex(DEFAULT_BRAND_COLOR) as Rgb);

  let ink = base;
  // Bounded: each step closes 18% of the remaining distance to black, so this
  // converges regardless of where it started.
  for (let i = 0; i < 12 && luminance(ink) > 0.22; i++) ink = mix(ink, BLACK, 0.18);

  return {
    base: toHex(base),
    onBase: luminance(base) > 0.45 ? "#111827" : "#ffffff",
    ink: toHex(ink),
    wash: toHex(mix(base, WHITE, 0.92)),
  };
}

/** The legally-binding name if there is one, otherwise what the company goes by. */
export function documentName(identity: Pick<CompanyIdentity, "name" | "legalName">): string {
  return identity.legalName?.trim() || identity.name;
}

/**
 * The company behind the document on screen. Shares its cache key with the
 * settings screen, so branding edited there is reflected in an open proposal
 * without a reload.
 */
export function useCompanyIdentity(slug: string) {
  return useQuery({
    queryKey: ["company", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].$get({ param: { slug } })),
  });
}

/** Kept in step with `LOGO_MAX_CHARS` in `apps/api/src/schemas.ts`. */
const LOGO_MAX_CHARS = 400_000;
const LOGO_MAX_PX = 600;

/**
 * Turns an uploaded file into the `data:` URI the API stores.
 *
 * Always re-encoded through a canvas rather than passed through: it bounds the
 * size a letterhead can reach, and it means an SVG — markup that would
 * otherwise be served back to every client who opens a proposal — lands in the
 * column as pixels or not at all.
 */
export async function readLogoFile(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, LOGO_MAX_PX / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Couldn't read that image.");
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  // PNG first, so a logo cut out on transparency stays cut out. A photographic
  // or very large one blows the column budget as PNG, and there a white matte
  // and JPEG is a better answer than refusing the upload.
  const png = canvas.toDataURL("image/png");
  if (png.length <= LOGO_MAX_CHARS) return png;

  ctx.globalCompositeOperation = "destination-over";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  const jpeg = canvas.toDataURL("image/jpeg", 0.85);
  if (jpeg.length > LOGO_MAX_CHARS) throw new Error("That image is too large — try a smaller one.");
  return jpeg;
}

/* ------------------------------------------------------------------------- *
 * Typography
 * ------------------------------------------------------------------------- */

/** Kept in step with `FONT_MAX_CHARS` in `apps/api/src/schemas.ts`. */
const FONT_MAX_CHARS = 1_000_000;

/**
 * The CSS family names the document uses. Fixed rather than derived from the
 * uploaded file: a family name taken from user input would need escaping
 * everywhere it was interpolated into CSS, and there is nothing to gain from
 * it — only one company's fonts are ever loaded at a time, and the rules are
 * rewritten wholesale when that changes.
 */
const DISPLAY_FAMILY = "DantriaBrandDisplay";
const BODY_FAMILY = "DantriaBrandBody";

/** Fallbacks matter more than usual here: a font that fails to decode must
 *  still leave a readable document rather than an empty page. */
const DISPLAY_FALLBACK = `"Inter Tight", ui-sans-serif, system-ui, sans-serif`;
const BODY_FALLBACK = "ui-sans-serif, system-ui, sans-serif";

/**
 * The four-byte tag each supported font format starts with.
 *
 * Sniffed from the file's own header rather than trusted from `File.type`,
 * which browsers leave empty or set to `application/octet-stream` for fonts
 * often enough that it cannot be relied on. Compared as a number because one
 * of the tags is `0x00010000` — as a string that is a run of NUL bytes, which
 * has no business being typed into a source file.
 *
 * Identifying the file also settles how to describe it: the same row carries
 * the `format()` hint the `@font-face` rule needs.
 */
const FONT_SIGNATURES = [
  { tag: 0x774f4632, mime: "font/woff2", format: "woff2" }, // wOF2
  { tag: 0x774f4646, mime: "font/woff", format: "woff" }, // wOFF
  { tag: 0x4f54544f, mime: "font/otf", format: "opentype" }, // OTTO
  { tag: 0x00010000, mime: "font/ttf", format: "truetype" }, // sfnt version 1.0
  { tag: 0x74727565, mime: "font/ttf", format: "truetype" }, // the legacy Apple tag
] as const;

export interface BrandFont {
  /** `data:font/...;base64,...` — what the column stores. */
  src: string;
  /** The uploaded file's name, shown so somebody can tell what is loaded. */
  name: string;
}

/**
 * Turns an uploaded font file into the `data:` URI the API stores.
 *
 * Unlike a logo there is no re-encoding step available — a browser cannot
 * subset or convert a font — so this validates and embeds rather than
 * transforming. That makes the size check the only defence against a
 * multi-megabyte family landing in a column every proposal reader downloads,
 * which is why it refuses rather than degrades.
 */
export async function readFontFile(file: File): Promise<BrandFont> {
  const buffer = await file.arrayBuffer();
  if (buffer.byteLength < 4) throw new Error("That file is empty.");

  const tag = new DataView(buffer).getUint32(0);
  const signature = FONT_SIGNATURES.find((s) => s.tag === tag);
  if (!signature) {
    throw new Error("That isn't a font file. Use a .woff2, .woff, .otf or .ttf.");
  }

  // Chunked: `String.fromCharCode(...bytes)` on a whole font blows the argument
  // limit and throws on exactly the large files this most needs to handle.
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  const src = `data:${signature.mime};base64,${btoa(binary)}`;

  if (src.length > FONT_MAX_CHARS) {
    throw new Error(
      "That font is too large to embed. A WOFF2 containing only the weights you " +
        "use is usually a tenth of the size.",
    );
  }

  return { src, name: file.name.replace(/\.[^.]+$/, "").slice(0, 120) };
}

export interface BrandTypography {
  /**
   * `font-family` for headings and the letterhead, or null to leave the app's
   * own default alone. Null rather than a copy of that default on purpose: a
   * document with no custom face should keep whatever the stylesheet says,
   * not a second definition of it that has to be kept in step.
   */
  display: string | null;
  /** `font-family` for running text and the money tables, or null as above. */
  body: string | null;
  /** `@font-face` rules for whichever slots are set — empty when neither is. */
  css: string;
  /** True when at least one custom face has to load before the document is
   *  what it will print as. */
  hasCustom: boolean;
}

/** Whether a stored value is a font we can actually build a rule from. Guards
 *  the document against a half-written or truncated column. */
function usableFont(src: string | null | undefined): src is string {
  return !!src && /^data:font\/(woff2|woff|ttf|otf);base64,/.test(src);
}

function formatOf(src: string): string {
  const mime = /^data:font\/([a-z0-9]+);/.exec(src)?.[1];
  return FONT_SIGNATURES.find((s) => s.mime === `font/${mime}`)?.format ?? "woff2";
}

/**
 * The document's typography, as CSS.
 *
 * Returns rules rather than applying them because the PDF export rasterizes
 * the document's DOM, and html2canvas resolves fonts through the *stylesheet*
 * — a face registered only through the `FontFace` JavaScript API is invisible
 * to it, and the export silently comes out in the fallback. `BrandFontFaces`
 * puts these into a real `<style>` element for that reason.
 */
export function brandTypography(
  identity: Pick<CompanyIdentity, "displayFontSrc" | "bodyFontSrc">,
): BrandTypography {
  const display = usableFont(identity.displayFontSrc) ? identity.displayFontSrc : null;
  const body = usableFont(identity.bodyFontSrc) ? identity.bodyFontSrc : null;

  // `font-display: block` rather than `swap`: a proposal that repaints from a
  // fallback into the real face while someone is reading it looks broken, and
  // the face is already in memory — there is no network wait to swap around.
  const face = (family: string, src: string) =>
    `@font-face{font-family:"${family}";src:url("${src}") format("${formatOf(src)}");font-display:block;}`;

  const rules: string[] = [];
  if (display) rules.push(face(DISPLAY_FAMILY, display));
  if (body) rules.push(face(BODY_FAMILY, body));

  return {
    display: display ? `"${DISPLAY_FAMILY}", ${DISPLAY_FALLBACK}` : null,
    body: body ? `"${BODY_FAMILY}", ${BODY_FALLBACK}` : null,
    css: rules.join("\n"),
    hasCustom: !!display || !!body,
  };
}

/** The id of the single style element the rules live in. One element, rewritten
 *  in place, so switching company can never leave the previous one's faces
 *  registered alongside the new ones. */
const FONT_STYLE_ID = "dantria-brand-fonts";

/**
 * Registers the company's faces in the document, and reports when they are
 * ready to be drawn.
 *
 * A `<style>` element rather than the `FontFace` API: the PDF export
 * rasterizes the document through html2canvas, which reads the stylesheet and
 * cannot see a face registered only in JavaScript. Kept in `document.head`
 * rather than inside the proposal, because a `<style>` inside the element
 * being captured is part of the subtree being cloned and is not reliably
 * applied to the clone.
 *
 * `false` while a face is still loading. The caller uses it to hold the export
 * back — a PDF rasterized mid-load comes out in the fallback typeface, which
 * is a document that looks finished and is wrong.
 */
export function useBrandFonts(typography: BrandTypography): boolean {
  const [ready, setReady] = useState(!typography.hasCustom);

  useEffect(() => {
    let element = document.getElementById(FONT_STYLE_ID) as HTMLStyleElement | null;
    if (!element) {
      element = document.createElement("style");
      element.id = FONT_STYLE_ID;
      document.head.appendChild(element);
    }
    element.textContent = typography.css;

    if (!typography.hasCustom) {
      setReady(true);
      return;
    }

    // Nothing requests a face until something is painted in it, so the families
    // are asked for explicitly rather than waiting on `document.fonts.ready`
    // alone — that resolves immediately when no request is outstanding yet.
    let cancelled = false;
    setReady(false);
    const wanted = [typography.display, typography.body].filter((f): f is string => !!f);
    Promise.all(wanted.map((family) => document.fonts.load(`16px ${family}`)))
      .catch(() => undefined)
      .then(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [typography.css, typography.hasCustom, typography.display, typography.body]);

  return ready;
}
